import {Component, HostListener, inject, OnDestroy, OnInit} from "@angular/core";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { LoginService } from "./services/login.service";
import { SideNavService } from "./components/nav-bar/Services/side-nav.service";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { RootRoutePaths } from "./models/routes-constants";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatDrawerMode, MatSidenavModule } from "@angular/material/sidenav";
import { ChatbotService } from "./modules/chat-commands/services/chatbot.service";
import { NavBarComponent } from "./components/nav-bar/nav-bar.component";
import { NgClass, NgIf } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { ChatbotPanelComponent } from "./modules/chat-commands/components/chatbot-panel/chatbot-panel.component";
import { CustomSpinnerComponent } from "./components/Shared/custom-spinner/custom-spinner.component";
import { AutosizeModule } from "ngx-autosize";
import { NoConnectionFoundComponent } from "./modules/shared/Components/no-connection-found/no-connection-found.component";
import { ConnectivityService } from "./modules/shared/Services/connectivity.service";
import { takeUntil, filter } from "rxjs/operators";
import { Subject } from "rxjs";
import { JitsiMeetingComponent } from "./modules/video-meeting/jitsi-meeting.component";
import { SideNavPanelComponent } from "./components/Shared/side-nav-panel/side-nav-panel.component";
import { WebSocketSharedService } from "./modules/conversation/services/websocket-shared.service";
import { AppNotification, PushPreviewContainerComponent, PushPreviewStore } from "./modules/notification-preview";
import { NOTIFICATIONCONST, objectify, RawNotification } from "./components/nav-bar/Models/notification.model";
import { NotificationsService } from "./components/nav-bar/Services/notifications.service";
import { SettingsSideNavPanelComponent } from "./components/Shared/settings-side-nav-panel/settings-side-nav-panel.component";
import { AuthSyncService } from "./services/auth-sync.service";

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: "YYYY/MM/DD",
    },
    display: {
        dateInput: "DD/MM/YYYY",
        monthYearLabel: "MMMM YYYY",
        dateAllyLabel: "LL",
        monthYearAllyLabel: "MMMM YYYY",
    },
};

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }],
    imports: [
        MatSidenavModule,
        AutosizeModule,
        NgIf,
        MatIconModule,
        NgClass,
        RouterOutlet,
        NavBarComponent,
        ChatbotPanelComponent,
        CustomSpinnerComponent,
        NoConnectionFoundComponent,
        JitsiMeetingComponent,
        SideNavPanelComponent,
        PushPreviewContainerComponent,
        SettingsSideNavPanelComponent,
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    isLoggedIn: boolean = false;
    isShowingIconsOnlyBar: boolean = false;
    sideNavMode: MatDrawerMode = "side";
    isUserDeviceOnline: boolean = true;
    currentRoute: string = '';
    isMeetPage: boolean = false;
    isSidenavOpened = false;
    private destroy$: Subject<void> = new Subject<void>();
    private prefetchTimeout: ReturnType<typeof setTimeout> | null = null;

    // Inject the Store so we can sync state
    private pushPreviewStore = inject(PushPreviewStore);
    private notificationsService = inject(NotificationsService);
    private authSyncService = inject(AuthSyncService);

    constructor(
        private loginService: LoginService,
        private sidenavService: SideNavService,
        private router: Router,
        private breakPointObserver: BreakpointObserver,
        private chatbotService: ChatbotService,
        private connectivityService: ConnectivityService,
        private sharedWebSocketAPI: WebSocketSharedService
    ) {
        // Responsive breakpoint observer for sidenav
        this.breakPointObserver
            .observe([
                Breakpoints.Web,
                Breakpoints.WebPortrait,
                Breakpoints.WebLandscape,
                Breakpoints.XLarge,
                Breakpoints.Large,
                Breakpoints.Tablet,
                Breakpoints.TabletLandscape,
                Breakpoints.TabletPortrait,
                Breakpoints.Medium,
                Breakpoints.XSmall,
                Breakpoints.Small,
                Breakpoints.Handset,
                Breakpoints.HandsetLandscape,
                Breakpoints.HandsetPortrait,
            ])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.sidenavService.close();
                },
            });

        // Initialize login state
        this.isLoggedIn = this.loginService.isLoggedIn();

        // Initialize currentRoute with the current URL for initial render
        // Use window.location.pathname because router.url may not be ready in constructor
        this.currentRoute = window.location.pathname;
        this.isMeetPage = this.isMeetPageCheck();

        // Subscribe to router events with proper cleanup
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.currentRoute = this.router.url;
                this.isMeetPage = this.isMeetPageCheck();
            });
    }

    isMeetPageCheck(): boolean {
        const meetRoute = `/${RootRoutePaths.MEET}`;
        return this.currentRoute.startsWith(meetRoute);
    }

    isSettingsRoute(): boolean {
        const settingsRoute = `/${RootRoutePaths.SETTINGS}`;
        return this.currentRoute.startsWith(settingsRoute);
    }

    /**
     * Check if current route is a document editor route (/documents/:id).
     * Only returns true when viewing/editing a specific document.
     * The document list page (/documents) uses the main app layout.
     */
    isDocumentRoute(): boolean {
        // Match /documents/:id pattern - must have content after /documents/
        const match = this.currentRoute.match(/^\/documents\/(.+)/);
        return match !== null && match[1].length > 0;
    }

    // React to browser tab becoming visible
    @HostListener("document:visibilitychange")
    onVisibilityChange(): void {
        // When the tab becomes visible again...
        if (document.visibilityState === "visible") {
            this.attemptSocketConnect();
        }
    }

    // Helper method to keep logic DRY
    private attemptSocketConnect(): void {
        // This single block of code now serves all triggers
        if (this.isUserDeviceOnline && this.isLoggedIn) {
            // console.log("Attempting to connect WebSocket...");
            // this.webSocketAPI.connect();
            this.sharedWebSocketAPI.connect().then(() => {
                // console.log("WebSocket connection attempt completed.");
            }).catch((error) => {
                console.error("WebSocket connection attempt failed:", error);
            });
        }
    }

    ngOnInit() {
        // Clear auth flags and initialize session storage via AuthSyncService
        this.authSyncService.clearAuthFlags();
        this.authSyncService.initializeSessionStorage();

        // Start listening for multi-tab auth sync (navigate on logout for main app)
        this.authSyncService.startListening({ onLogout: 'navigate', onLogin: 'navigate' });

        // Monitor connectivity status
        this.connectivityService.isUserDeviceOnline$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (status) => {
                this.isUserDeviceOnline = status;
                this.attemptSocketConnect();
            },
        });

        // Monitor login state changes
        this.loginService.isUserLoggedIn$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isLoggedIn = this.loginService.isLoggedIn();
            if (this.isLoggedIn) {
                this.attemptSocketConnect();
            } else {
                this.sharedWebSocketAPI.closeConnection();
                this.loginService.attemptToDeleteFCMToken();
            }
        });

        // Monitor sidenav state
        this.sidenavService.sidenavOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
            this.isShowingIconsOnlyBar = !open;
        });

        // Prefetch commonly used components
        this.prefetchSomeLazyLoadedComponents();
    }

    prefetchSomeLazyLoadedComponents(): void {
        // Store timeout reference for cleanup
        this.prefetchTimeout = setTimeout(() => {
            import("../app/modules/task-management/update-task/work-item-dialog/work-item-dialog.component")
                .then(() => console.log('WID preloaded'))
                .catch(err => console.error('Preload failed:', err));
        }, 1000); // loading work item dialog component after 1 second
    }

    toggleSidenav(): void {
        this.sidenavService.toggle();
    }

    startChatInteraction() {
        this.chatbotService.toggleChatbot();
    }

    @HostListener("window:beforeunload")
    unloadHandler(): void {
        // This will reliably execute our cleanup logic when the tab/browser is closed.
        // this.sharedWebSocketAPI.closeConnection();
    }

    ngOnDestroy() {
        // Stop listening for auth sync events
        this.authSyncService.stopListening();

        // Clear prefetch timeout if still pending
        if (this.prefetchTimeout) {
            clearTimeout(this.prefetchTimeout);
            this.prefetchTimeout = null;
        }

        // Complete all subscriptions
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSidenavMouseEnter() {
        if(this.isShowingIconsOnlyBar) {
            this.isSidenavOpened = true;
        }
    }

    onSidenavMouseLeave() {
        if(this.isShowingIconsOnlyBar) {
            this.isSidenavOpened = false;
        }
    }

    protected onOpenNotificationTray() {
        // Inform the Preview Store that the tray is open
        // This ensures the preview cards disappear immediately so they don't overlap the tray.
        this.pushPreviewStore.setTrayOpen(true);
    }

    protected onPreviewClick(notification: AppNotification) {
        let rawNotification = notification.rawNotification as RawNotification;
        this.objectifyAndMarkAsReadAndHandleNotificationClick(rawNotification, notification.payload);
    }

    objectifyAndMarkAsReadAndHandleNotificationClick(notification: RawNotification, payload: any) {
        notification.isRead = "true";
        let result = objectify(JSON.stringify(payload));
        notification.payload = JSON.parse(JSON.stringify(result));
        if (notification.notificationType != NOTIFICATIONCONST.NotificationType.CHAT_MESSAGE_ALERTS) {
            this.notificationsService.markAsReadNotification(result.notificationId, notification).subscribe(
                () => {
                    this.notificationsService.updateNotificationCount();
                },
                (error) => {
                    console.log("error", error);
                },
            );
        }
        this.notificationsService.handleNotificationClick(notification).then(r => {});
    }
}
